import React, { useState, useEffect, useMemo } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { Carousel, Modal, Button } from "react-bootstrap";
import {
  FaBookOpen,
  FaFileDownload,
  FaClock,
  FaMapMarkerAlt,
} from "react-icons/fa";
import menu from "../Images/Menu.jpg";
import { BASEAPIURL, token } from "../env";

const placeholderImage =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAACklEQVR4nGMAAQAABQABDQottAAAAABJRU5ErkJggg==";

const BranchDetails = () => {
  const { locName } = useParams();
  const { state } = useLocation();
  const [facilityData, setFacilityData] = useState(null);
  const [isModalVisible, setModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const lowerLocName = useMemo(
    () => (locName || "").toLowerCase().trim(),
    [locName]
  );
  const galleryImages = useMemo(
    () =>
      facilityData?.Facility_Image?.length > 0
        ? facilityData.Facility_Image
        : state?.images || [placeholderImage],
    [facilityData, state]
  );
  const facilityImages = useMemo(
    () =>
      facilityData?.Facility_DesImage?.length > 0
        ? facilityData.Facility_DesImage
        : state?.Facility_DesImage || [],
    [facilityData, state]
  );

  // Check if booking is enabled - only show button if value is explicitly true
  const isBookingEnabled = useMemo(() => {
    return facilityData?.IsBooking === true; // Only true if explicitly true
  }, [facilityData]);

  useEffect(() => {
    if (!state?.id) return;

    const fetchFacility = async () => {
      setIsLoading(true);
      setError(null);
      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");

      const requestOptions = {
        method: "GET",
        headers: myHeaders,
        redirect: "follow",
      };

      try {
        const response = await fetch(
          `${BASEAPIURL}/GetFacilityByID?FacilityID=${state.id}&Token=${token}`,
          requestOptions
        );
        if (!response.ok) throw new Error("Failed to fetch facility data");
        const result = await response.json();
        if (!result?.data?.[0]?.Facility_ID)
          throw new Error("Invalid facility data");
        setFacilityData(result.data[0]);
      } catch (error) {
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFacility();
  }, [state?.id]);

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = menu;
    link.download = "menu.jpg";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (isLoading) {
    return (
      <>
        <div className="row p-4 g-3">
          {Array.from({ length: 1 }).map((_, i) => (
            <div className="col-12" key={i}>
              {/* 🔹 Image Placeholder */}
              <div className="placeholder-glow">
                <div
                  className="card-img-top bg-secondary placeholder position-relative"
                  style={{
                    height: "70vh", // Default height for medium/large screens
                  }}
                ></div>
              </div>
            </div>
          ))}
        </div>
        <div className="row p-4 g-3">
          {Array.from({ length: 1 }).map((_, i) => (
            <div className="col-md-12 col-sm-6" key={i}>
              <div className="card shadow-sm border-0">
                {/* 🔹 Image Placeholder */}
                <div className="placeholder-glow">
                  <div
                    className="card-img-top bg-secondary placeholder"
                    style={{ height: "200px" }}
                  ></div>
                </div>

                {/* 🔹 Text Placeholder */}
                <div className="card-body">
                  <h5 className="card-title placeholder-glow mb-3">
                    <span className="placeholder col-6"></span>
                  </h5>
                  <p className="card-text placeholder-glow">
                    <span className="placeholder col-7"></span>
                    <span className="placeholder col-4"></span>
                    <span className="placeholder col-6"></span>
                    <span className="placeholder col-4"></span>
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </>
    );
  }

  if (error || (!state && !facilityData)) {
    return (
      <div className="text-center mt-5">
        <h5>{error || "No branch data available."}</h5>
        <Button variant="primary" onClick={() => window.history.back()}>
          Go Back
        </Button>
      </div>
    );
  }

  const title =
    facilityData?.Facility_Name ||
    facilityData?.Facility_EC_Name ||
    state?.title ||
    "Unnamed Branch";
  const desc =
    facilityData?.Facility_Description ||
    state?.desc ||
    "No description available.";
  const branch = state?.branch || [];
  const address = state?.address || "";
  const contact = state?.contact || "";

  return (
    <div className="">
      <Carousel interval={4000} controls fade pause="hover">
        {galleryImages.map((img, idx) => (
          <Carousel.Item key={`gallery-${idx}`}>
            <img
              className="d-block w-100"
              src={img}
              alt={`Gallery image ${idx + 1} for ${title}`}
              loading="lazy"
              style={{ height: "80vh" }}
              onError={(e) => {
                e.currentTarget.src = placeholderImage;
              }}
            />
          </Carousel.Item>
        ))}
      </Carousel>

      {lowerLocName === "restaurant" && (
        <div className="text-center my-4">
          <Button
            variant="outline-danger"
            onClick={() => setModalVisible(true)}
            aria-label="View restaurant menu"
          >
            <FaBookOpen className="me-2" /> View Menu
          </Button>
        </div>
      )}

      <div className="container my-4">
        <div className="card shadow-sm border-0">
          <div className="card-body p-4">
            <h2 className="card-title text-center mb-4 text-primary fw-bold">
              {title}
            </h2>

            {address && (
              <p className="text-muted text-center d-flex align-items-center justify-content-center gap-2 mb-3">
                <FaMapMarkerAlt aria-hidden="true" /> {address}
              </p>
            )}

            {contact && (
              <p className="text-muted text-center mb-3">
                <strong>Contact:</strong> {contact}
              </p>
            )}

            <p
              className="card-text text-justify lh-lg"
              style={{ fontSize: "20px" }}
            >
              {desc}
            </p>

            {facilityImages.length > 0 && (
              <div className="mt-4">
                <h5 className="text-center mb-3">Facility Highlights</h5>
                <div className="d-flex flex-wrap justify-content-center gap-3">
                  {facilityImages.map((image, index) => (
                    <img
                      key={image.id || `facility-image-${index}`}
                      src={image.src || image}
                      alt={`Facility highlight ${index + 1} for ${title}`}
                      className="rounded shadow-sm h-50 facility-image"
                      loading="lazy"
                    />
                  ))}
                </div>
              </div>
            )}

            {branch.length > 0 && (
              <div className="">
                <h5 className="text-center mb-3">Available Branches</h5>
                <div className="row g-3">
                  {branch.map((b, idx) => (
                    <div className="col-md-6" key={`branch-${idx}`}>
                      <div className="border rounded p-3 bg-light">
                        <h6 className="fw-bold mb-2">
                          {b.branchName || "Branch"}
                        </h6>
                        <p className="mb-1">
                          {b.branchDetails || "No details"}
                        </p>
                        {b.timming && (
                          <p className="text-muted mb-0">
                            <FaClock className="me-1" aria-hidden="true" />{" "}
                            {b.timming}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {isBookingEnabled && ( // Only show if explicitly true
              <div className="text-center mt-4">
                <Link
                  to="/Login"
                  className="btn btn-primary px-4 py-2"
                  aria-label="Make a booking inquiry"
                >
                  Booking Inquiry
                </Link>
              </div>
            )}

            {["lawn & hall", "accommodation"].includes(lowerLocName) && (
              <div className="text-center mt-3">
                <small className="text-muted">
                  Or call us directly for urgent bookings.
                </small>
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal
        show={isModalVisible}
        onHide={() => setModalVisible(false)}
        centered
        size="lg"
        aria-labelledby="restaurant-menu-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title id="restaurant-menu-modal">Restaurant Menu</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          <img
            src={menu}
            alt="Restaurant menu"
            className="img-fluid rounded modal-menu-image"
            loading="lazy"
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="success"
            onClick={handleDownload}
            aria-label="Download menu"
          >
            <FaFileDownload className="me-1" /> Download
          </Button>
          <Button
            variant="secondary"
            onClick={() => setModalVisible(false)}
            aria-label="Close menu modal"
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default BranchDetails;
